import { useCallback, useState, useMemo } from 'react';
import { each } from 'underscore';
import { MaxAddAssetJobInParell, CSMLAssetSource } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { runJobsInParallelWithRateLimit } from '@bingads-webui-universal/parallel-job-with-rate-limit';

export const useSourceImages = ({
  dataService, logger, navigation, perfMarker,
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [showBlocker, setShowBlocker] = useState(false);
  const validImages = useMemo(() => (Array.isArray(selectedImages) ? selectedImages.filter(({ id }) => !!id) : []), [selectedImages]);
  const selectedImageUrls = useMemo(() => validImages.map(({ id }) => id), [validImages]);

  const onStockClickSelect = useCallback((handleJobErrors) => {
    const stockImagePerfMarker = perfMarker && perfMarker.createChild('stockImagePicker');
    if (stockImagePerfMarker) {
      stockImagePerfMarker.willInit();
    }
    const jobErrors = [];
    const handleJobError = (err) => {
      jobErrors.push(err);
    };

    const saveOperations = [];

    each(selectedImageUrls, (id) => {
      saveOperations.push(() => dataService.uploadStockImage({ stockImageId: id }));
    });
    setShowBlocker(true);
    runJobsInParallelWithRateLimit({
      jobsToRateLimit: saveOperations,
      maxJobsInParallel: MaxAddAssetJobInParell,
      handleJobError,
    }).then((values) => {
      const imageIds = values || [];
      logger.logTrace('Add Media Panel: saved stock images to folder', { imageIds: imageIds.join(','), count: imageIds.length });
      navigation.goBack({ reload: true });
    }).finally(() => {
      setSelectedImages([]);
      handleJobErrors(jobErrors);
      setShowBlocker(false);
      if (stockImagePerfMarker) {
        stockImagePerfMarker.done();
      }
    });
  }, [dataService, logger, navigation, selectedImageUrls]);

  const onInvestClickSelect = useCallback((handleJobErrors) => {
    const investImagePerfMarker = perfMarker && perfMarker.createChild('investImagePicker');
    if (investImagePerfMarker) {
      investImagePerfMarker.willInit();
    }
    const jobErrors = [];
    const handleJobError = (err) => {
      jobErrors.push(err);
    };

    const saveOperations = [];

    each(validImages, ({ id, url }) => {
      saveOperations.push(() => dataService.uploadInvestImage({ investImageId: id, imageSrc: url }));
    });

    setShowBlocker(true);
    runJobsInParallelWithRateLimit({
      jobsToRateLimit: saveOperations,
      maxJobsInParallel: MaxAddAssetJobInParell,
      handleJobError,
    }).then((values) => {
      const imageIds = values || [];
      logger.logTrace('Add Media Panel: saved invest images to folder', { imageIds: imageIds.join(','), count: imageIds.length });
      navigation.goBack({ reload: true });
    }).finally(() => {
      setSelectedImages([]);
      handleJobErrors(jobErrors);
      setShowBlocker(false);
      if (investImagePerfMarker) {
        investImagePerfMarker.done();
      }
    });
  }, [dataService, logger, navigation, validImages]);

  const onWebsiteClickSelect = useCallback((handleJobErrors) => {
    const jobErrors = [];
    const websiteImagePerfMarker = perfMarker && perfMarker.createChild('websiteImagePicker');
    if (websiteImagePerfMarker) {
      websiteImagePerfMarker.willInit();
    }
    const handleJobError = (err) => {
      jobErrors.push(err);
    };

    const uploadOperations = [];
    each(selectedImageUrls, (url) => {
      uploadOperations.push(() => dataService.uploadImageByUrl({ imageUrl: url, sourceType: CSMLAssetSource.Website }));
    });
    setShowBlocker(true);
    runJobsInParallelWithRateLimit({
      jobsToRateLimit: uploadOperations,
      maxJobsInParallel: MaxAddAssetJobInParell,
      handleJobError,
    }).then((values) => {
      const imageIds = values || [];
      logger.logTrace('Add Media Panel: saved website images to folder', { imageIds: imageIds.join(','), count: imageIds.length });
      navigation.goBack({ reload: true });
    })
      .finally(() => {
        setSelectedImages([]);
        handleJobErrors(jobErrors);
        setShowBlocker(false);
        if (websiteImagePerfMarker) {
          websiteImagePerfMarker.done();
        }
      });
  }, [dataService, logger, navigation, selectedImageUrls]);

  return {
    selectedImages,
    showBlocker,
    setSelectedImages,
    onWebsiteClickSelect,
    onStockClickSelect,
    onInvestClickSelect,
  };
};
